import { fromDateInput, toDateInput } from '@crm/shared';
import { Label, Modal, Select, Textarea } from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormField } from '@/components';
import { useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { type FinanceEntry, useFinanceCategories, useUpdateFinanceEntry } from '../api';

const FORM_ID = 'finance-entry-form';

const entryFormSchema = z.object({
  description: z.string().min(2, 'Descreva a conta').max(160),
  amount: z.coerce.number().positive('Informe um valor maior que zero'),
  dueDate: z.string().min(1, 'Informe o vencimento'),
  categoryId: z.string(),
  unitId: z.string(),
  notes: z.string().max(2000),
});

type EntryFormValues = z.infer<typeof entryFormSchema>;

/**
 * Ajuste de um vencimento ja previsto - a conta da luz que veio diferente do
 * cadastrado, por exemplo. Mexe so neste mes; o cadastro fica como esta.
 */
export function EntryDialog({
  entry,
  onClose,
}: {
  entry: FinanceEntry | null;
  onClose: () => void;
}) {
  const { user } = useSession();
  const units = user?.units ?? [];

  const updateEntry = useUpdateFinanceEntry();
  const categories = useFinanceCategories({ type: entry?.type, onlyActive: true });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entryFormSchema),
    defaultValues: {
      description: '',
      amount: 0,
      dueDate: toDateInput(new Date()),
      categoryId: '',
      unitId: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (!entry) return;

    reset({
      description: entry.description,
      amount: entry.amount,
      dueDate: toDateInput(entry.dueDate),
      categoryId: entry.categoryId ?? '',
      unitId: entry.unitId ?? '',
      notes: entry.notes ?? '',
    });
  }, [entry, reset]);

  function onSubmit(values: EntryFormValues) {
    if (!entry) return;

    updateEntry.mutate(
      {
        id: entry.id,
        input: {
          description: values.description,
          amount: values.amount,
          dueDate: fromDateInput(values.dueDate),
          categoryId: values.categoryId || undefined,
          unitId: values.unitId || undefined,
          notes: values.notes || undefined,
        },
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      open={entry !== null}
      onClose={onClose}
      title="Editar vencimento"
      subtitle="Vale so para este vencimento - o cadastro da conta nao muda."
      size="medium"
      actions={[
        { label: 'Cancelar', variant: 'ghost', onClick: onClose },
        { label: 'Salvar', type: 'submit', formId: FORM_ID, loading: updateEntry.isPending },
      ]}
    >
      <form id={FORM_ID} className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
        {updateEntry.error ? (
          <p className="text-sm text-destructive">{toErrorMessage(updateEntry.error)}</p>
        ) : null}

        <FormField
          label="Descricao"
          error={errors.description?.message}
          {...register('description')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="Valor (R$)"
            type="number"
            step="0.01"
            min="0"
            error={errors.amount?.message}
            {...register('amount')}
          />
          <FormField
            label="Vencimento"
            type="date"
            error={errors.dueDate?.message}
            {...register('dueDate')}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="entry-category">Categoria</Label>
            <Select id="entry-category" {...register('categoryId')}>
              <option value="">Sem categoria</option>
              {(categories.data?.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </div>

          {units.length > 1 ? (
            <div className="space-y-2">
              <Label htmlFor="entry-unit">Unidade</Label>
              <Select id="entry-unit" {...register('unitId')}>
                <option value="">Empresa toda</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="entry-notes">Observacoes</Label>
          <Textarea id="entry-notes" {...register('notes')} />
        </div>
      </form>
    </Modal>
  );
}
